/*
 * VNCtalk - an enterprise real-time communication solution including chat, video and audio conferencing, screen sharing, voice messaging, file sharing, broadcasts, document collaboration and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { WindowFocusStatus, SetAppSettings, UpdateNetworkInformation, SetSelectedChatBackgroundPreview } from "./../../actions/app";
import { Injectable  } from "@angular/core";
import { DomSanitizer , SafeHtml } from "@angular/platform-browser";
import { CommonUtil } from "../../talk/utils/common.util";
import { TranslateService } from "@ngx-translate/core";
import { XmppService } from "../../talk/services/xmpp.service";
import {
  LogoutSuccess,
  Logout,
  NotifyOptionChange,
  OnlineStatus,
  SetBaseApiUrl,
  SetFirebaseToken, SetFontSize
} from "../../actions/app";
import { Store } from "@ngrx/store";
import {
  getIsAppOnline,
  getIsConnectedXMPP,
  getUserJID,
  RootState,
  getBaseApiUrl,
  getIsLoggedIn,
  getAppSettings
} from "../../reducers";
import { AuthService } from "./auth.service";
import { empty, fromEvent, merge, Observable, timer } from "rxjs";
import { Subject } from "rxjs";
import { ConstantsUtil } from "../../talk/utils/constants.util";
import { Broadcaster } from "../../talk/shared/providers/broadcaster.service";
import { environment } from "../../environments/environment";
import {
  getConversations,
  getSelectedConversation,
  getTotalUnreadCount,
  TalkRootState,
  getConversationUnreadCount
} from "../../talk/reducers";
import {
  ConversationResetUnread
} from "../../talk/actions/conversation";
import { AppSettings } from "../../talk/models/app-settings.model";
import { BehaviorSubject } from "rxjs";
import { ConversationService } from "app/talk/services/conversation.service";
import { ConfigService } from "app/config.service";
import { NotificationService } from "app/talk/services/notification.service";
import { DatabaseService } from "app/talk/services/db/database.service";
import { ElectronService } from "../providers/electron.service";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { distinctUntilChanged, filter, map, mapTo, switchMap, take, tap } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { LoggerService } from "./logger.service";

@Injectable()
export class AppService {
  private isCordovaOrElectron = environment.isCordova || environment.isElectron;
  private _isOnline: boolean;
  private displayUnread: boolean = false;

  private _backButtonListener = new Subject<any>();
  private headers: HttpHeaders;
  _videoMiniRoundWindow = new Subject<any>();

  public get isAppOnline(): boolean {
    return this._isOnline;
  }

  constructor(private domSanitizer: DomSanitizer,
              private translate: TranslateService,
              private xmppService: XmppService,
              private notificationService: NotificationService,
              private authService: AuthService,
              private broadcaster: Broadcaster,
              private store: Store<RootState>,
              private storeTalk: Store<TalkRootState>,
              private databaseService: DatabaseService,
              private dialog: MatDialog,
              private logger: LoggerService,
              private configService: ConfigService,
              private electronService: ElectronService,
              private conversationService: ConversationService,
              private http: HttpClient
  ) {
    this.pullServerURLFromStorage();
    this.pullFirebaseTokenFromStorage();

    this.checkInternetConnection();
    this.checkWindowFocused();

    this.setupBackButtonListener();

    this.store.select(getIsAppOnline).subscribe(online => this._isOnline = online);
    // network changes
    merge(
      fromEvent(window, "online").pipe(mapTo(true)),
      fromEvent(window, "offline").pipe(mapTo(false))
    ).subscribe(this.notifyConnectionStatus.bind(this));
      this.importLibs();
  }

  async importLibs() {
    this.logger.info("importLibs", window.wdtEmojiBundle);
  }

  private notifyConnectionStatus(isConnected: boolean) {
    this.store.dispatch(new OnlineStatus(isConnected));

    let connectionType = "unknown";
    if (navigator.connection) {
      connectionType = navigator.connection.type ? navigator.connection.type : navigator.connection.effectiveType || "unknown";
    }
    this.logger.info("[AppService][notifyConnectionStatus] isOnline: ", isConnected, connectionType, navigator.connection);
    //
    this.store.dispatch(new UpdateNetworkInformation({
      onlineState: isConnected,
      connectionType: connectionType
    }));
  }

  getAppSettings() {
    return this.store.select(getAppSettings);
  }

  private checkInternetConnection() {
    this.notifyConnectionStatus(navigator.onLine);
  }

  private setupBackButtonListener() {
    document.addEventListener("backbutton" , (event) => {
      this._backButtonListener.next(event);
    });
  }

  public onLoggedIn(): Observable<boolean> {
    return this.store.select(getIsLoggedIn).pipe(map(v => !!v), distinctUntilChanged(), filter(v => v));
  }

  updateVideoCallSmallView(appOptions): Observable<any> {
    const response = new Subject<any>();
    const newSettings = appOptions;
    this.store.dispatch(new SetAppSettings(newSettings));
    this.xmppService.updatePrivateDocuments(newSettings).subscribe(updatedSettings => {
      response.next(updatedSettings);
    });
    return response.asObservable().pipe(take(1));
  }

  updateSettings(notifyOptionType, appOptions: AppSettings = {}): Observable<any> {
    const response = new Subject<any>();
    let oldStoredSettings: AppSettings = {};
    try {
      oldStoredSettings = JSON.parse(localStorage.getItem("privatexmldoc"));
    } catch (error) {

    }
    const notifyOptions = environment.isCordova ?
                                {"notification": notifyOptionType} :
                                {"desktopNotification": notifyOptionType};
    let newSettings = { ...notifyOptions, ...appOptions };
    if (environment.isCordova) {
      // keep setting for desktopNotification when adjusting settings on mobile
      if (!!oldStoredSettings && !!oldStoredSettings.desktopNotification) {
        newSettings["desktopNotification"] = oldStoredSettings.desktopNotification;
      }
    } else {
      // keep mobile notification settings when adjusting settings on desktop
      if (!!oldStoredSettings && !!oldStoredSettings.notification) {
        newSettings["notification"] = oldStoredSettings.notification;
      }
    }
    if ((Object.keys(newSettings).indexOf("selectedChatBackground") === -1) && !!oldStoredSettings && !!oldStoredSettings.selectedChatBackground) {
      newSettings.selectedChatBackground = oldStoredSettings.selectedChatBackground;
    }
    this.logger.info("[AppService][updateSettings]", notifyOptionType, notifyOptions, appOptions);
    this.logger.info("[AppService][updateSettings] newSettings: ", newSettings);
    this.store.dispatch(new SetAppSettings(newSettings));
    this.xmppService.updatePrivateDocuments(newSettings).subscribe(updatedSettings => {
      const settings = CommonUtil.getAppSettings(updatedSettings);
      this.store.dispatch(new SetAppSettings(settings));
      this.store.dispatch(new NotifyOptionChange(notifyOptionType));
      response.next(updatedSettings);
    });

    return response.asObservable().pipe(take(1));
  }

  setFirebaseToken(token: string): Observable<boolean> {
    const osSlug = CommonUtil.isOnIOS() ? "ios" : "android";

    return this.notificationService.storeFCMToken(token, device.uuid, osSlug).pipe(tap(() => {
      this.logger.info("[AppService][setFirebaseToken]", osSlug, token);
      this.pushFirebaseTokenToStore(token);
    }));
  }

  deleteFirebaseToken(): Observable<boolean> {
    return this.notificationService.deleteFCMToken(device.uuid).pipe(tap(() => {
      this.logger.info("[AppService][deleteFirebaseToken] device: ", device.uuid);
      this.pushFirebaseTokenToStore("");
    }));
  }

  changeServerUrl(url: string): Observable<boolean> {
    const response = new Subject<boolean>();

    this.authService.changeServerUrl(url).subscribe(data => {
      this.logger.info("[changeServerUrl]", data);
      if (data.jitsiURL) {
        this.store.dispatch(new SetBaseApiUrl(url));
        localStorage.setItem(ConstantsUtil.KEY_SERVER_URL , url);
        response.next(true);
      } else {
        response.next(false);
      }
    } , error => {
      this.logger.sentryErrorLog("[changeServerUrl] error", error);
      this.logger.error("[changeServerUrl] error", error);
      response.error(error);
    });


    return response.asObservable().pipe(take(1));
  }

  public sanitizeHTML(inputHTML: string): string {
    const escapedHTML = CommonUtil.escapeHTMLString(inputHTML);
    return this.parseLinkToHumanreadable(escapedHTML);
  }

  public sanitizeHTMLAndBypassSecurity(inputHTML: string): SafeHtml {
    const escapedHTML = CommonUtil.escapeHTMLString(inputHTML);
    const humanReadableHTML = this.parseLinkToHumanreadable(escapedHTML);
    return this.domSanitizer.bypassSecurityTrustHtml(humanReadableHTML);
  }

  public parseLinkToHumanreadable(text: string): string {
    text = this._addPrefixForMessageWithLink(text);

    // this.logger.info("parseLinkToHumanreadable", text);

    let translation;
    if (this.configService.getConfiguredLang() !== "de") {
      translation = {
        PHOTO: "Photo",
        VOICE_MESSAGE: "Voice Message",
        "LINK": "Link",
        AUDIO: "Audio",
        VIDEO_EXTENSION: "Video",
        MISSED_CALL: "Missed call",
        ENDED_CALL: "Ended call",
        UPLOADING_FILE: "Uploading.."
      };
    } else {
      translation = {
        PHOTO: "Foto",
        VOICE_MESSAGE: "Sprachnachricht",
        "LINK": "Verkn\u00fcpfung",
        AUDIO: "Sprachanruf",
        VIDEO_EXTENSION: "Video",
        MISSED_CALL: "Missed call",
        ENDED_CALL: "Ended call",
        UPLOADING_FILE: "Wird hochgeladen.."
      };
    }

    if (text.startsWith("blob:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>Icon/product/attachment</title>
        <g id="Icon/product/attachment" stroke="none" stroke-width="1" fill-rule="evenodd">
            <g id="Icon-24px" transform="translate(2.000000, 7.000000)" >
                <path d="M0,5.5 C0,2.46 2.46,0 5.5,0 L16,0 C18.21,0 20,1.79 20,4 C20,6.21 18.21,8 16,8 L7.5,8 C6.12,8 5,6.88 5,5.5 C5,4.12 6.12,3 7.5,3 L15,3 L15,5 L7.41,5 C6.86,5 6.86,6 7.41,6 L16,6 C17.1,6 18,5.1 18,4 C18,2.9 17.1,2 16,2 L5.5,2 C3.57,2 2,3.57 2,5.5 C2,7.43 3.57,9 5.5,9 L15,9 L15,11 L5.5,11 C2.46,11 0,8.54 0,5.5 L0,5.5 Z" id="Shape"></path>
            </g>
        </g>
      </svg>`;
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-attachment">${svgIcon}</span>${translation.UPLOADING_FILE}</div>`;
      this.translate.get("UPLOADING_FILE")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-attachment">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==foto:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>Icon/product/img-new</title>
        <g id="Icon/product/img-new" stroke="none" stroke-width="1"  fill-rule="evenodd">
            <path d="M19,3.5 C20.6568542,3.5 22,4.84314575 22,6.5 L22,6.5 L22,18.5 C22,20.1568542 20.6568542,21.5 19,21.5 L19,21.5 L5,21.5 C3.34314575,21.5 2,20.1568542 2,18.5 L2,18.5 L2,6.5 C2,4.84314575 3.34314575,3.5 5,3.5 L5,3.5 Z M15.121,12.756 L11.7452122,17.3218537 C11.4357207,17.7403706 10.8629658,17.846786 10.4275814,17.5854256 L10.329729,17.5185577 L7.916,15.654 L4.88337887,19.4932723 L4.88337887,19.4932723 L5,19.5 L19,19.5 C19.0439873,19.5 19.0873112,19.4971599 19.1297985,19.4916531 L15.121,12.756 Z M19,5.5 L5,5.5 C4.44771525,5.5 4,5.94771525 4,6.5 L4,6.5 L4,17.394 L6.99595535,13.6331785 C7.3137239,13.2512143 7.85795191,13.1646884 8.2747999,13.4125012 L8.37615338,13.4814423 L10.744,15.311 L14.3724349,10.405419 C14.76186,9.87881006 15.5321201,9.86919209 15.9396018,10.353342 L16.0160407,10.4567487 L20,17.064 L20,6.5 C20,5.98716416 19.6139598,5.56449284 19.1166211,5.50672773 L19,5.5 Z" id="Combined-Shape"></path>
        </g>
      </svg>`;
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-photo">${svgIcon}</span>${translation.PHOTO}</div>`;
      this.translate.get("PHOTO")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-photo">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==link:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>Icon/product/insert-link</title>
        <g id="Icon/product/insert-link" stroke="none" stroke-width="1" fill-rule="evenodd">
            <g id="Icon-24px" transform="translate(2.000000, 7.000000)">
                <path d="M1.9,5 C1.9,3.29 3.29,1.9 5,1.9 L9,1.9 L9,0 L5,0 C2.24,0 0,2.24 0,5 C0,7.76 2.24,10 5,10 L9,10 L9,8.1 L5,8.1 C3.29,8.1 1.9,6.71 1.9,5 L1.9,5 Z M6,6 L14,6 L14,4 L6,4 L6,6 L6,6 Z M15,0 L11,0 L11,1.9 L15,1.9 C16.71,1.9 18.1,3.29 18.1,5 C18.1,6.71 16.71,8.1 15,8.1 L11,8.1 L11,10 L15,10 C17.76,10 20,7.76 20,5 C20,2.24 17.76,0 15,0 L15,0 Z" id="Shape"></path>
            </g>
        </g>
      </svg>`;
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-link">${svgIcon}</span>${translation.LINK}</div>`;
      this.translate.get("LINK")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container con-type-link">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==voicemessage:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>A111215C-7164-47E7-BC4C-29892FDFCEB7</title>
        <g id="Symbols" stroke="none" stroke-width="1" fill-rule="evenodd">
            <g id="Icon/product/voicemessge-new-01">
                <path d="M14.5,6 C15.3284271,6 16,6.67157288 16,7.5 L16,17.5 C16,18.3284271 15.3284271,19 14.5,19 C13.6715729,19 13,18.3284271 13,17.5 L13,7.5 C13,6.67157288 13.6715729,6 14.5,6 Z M9.5,7 C10.3284271,7 11,7.67157288 11,8.5 L11,16.5 C11,17.3284271 10.3284271,18 9.5,18 C8.67157288,18 8,17.3284271 8,16.5 L8,8.5 C8,7.67157288 8.67157288,7 9.5,7 Z M19.5,9 C20.3284271,9 21,9.67157288 21,10.5 L21,14.5 C21,15.3284271 20.3284271,16 19.5,16 C18.6715729,16 18,15.3284271 18,14.5 L18,10.5 C18,9.67157288 18.6715729,9 19.5,9 Z M4.5,10 C5.32842712,10 6,10.6715729 6,11.5 L6,13.5 C6,14.3284271 5.32842712,15 4.5,15 C3.67157288,15 3,14.3284271 3,13.5 L3,11.5 C3,10.6715729 3.67157288,10 4.5,10 Z" id="Combined-Shape"></path>
            </g>
        </g>
      </svg>`;
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-voice">${svgIcon}</span>${translation.VOICE_MESSAGE}</div>`;
      this.translate.get("VOICE_MESSAGE")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-voice">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==audio:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <title>Icon/product/audiotrack</title>
          <g id="Icon/product/audiotrack" stroke="none" stroke-width="1" fill-rule="evenodd">
              <g id="Icon-24px" transform="translate(6.000000, 3.000000)">
                  <path d="M6,0 L6,9.28 C5.53,9.11 5.03,9 4.5,9 C2.01,9 0,11.01 0,13.5 C0,15.99 2.01,18 4.5,18 C6.81,18 8.7,16.25 8.95,14 L9,14 L9,3 L13,3 L13,0 L6,0 L6,0 Z" id="Shape"></path>
              </g>
          </g>
      </svg>`;
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-audiotrack">${svgIcon}</span>${translation.AUDIO}</div>`;
      this.translate.get("AUDIO")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-audiotrack">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==video:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <title>Icon/product/camera-new</title>
          <g id="Icon/product/camera-new" stroke="none" stroke-width="1" fill-rule="evenodd">
              <path d="M12,5 C14.1421954,5 15.8910789,6.68396847 15.9951047,8.80035966 L16,9 L16,15 C16,17.1421954 14.3160315,18.8910789 12.1996403,18.9951047 L12,19 L5,19 C2.85780461,19 1.10892112,17.3160315 1.00489531,15.1996403 L1,15 L1,9 C1,6.85780461 2.68396847,5.10892112 4.80035966,5.00489531 L5,5 L12,5 Z M12,7 L5,7 C3.9456382,7 3.08183488,7.81587779 3.00548574,8.85073766 L3,9 L3,15 C3,16.0543618 3.81587779,16.9181651 4.85073766,16.9945143 L5,17 L12,17 C13.0543618,17 13.9181651,16.1841222 13.9945143,15.1492623 L14,15 L14,9 C14,7.9456382 13.1841222,7.08183488 12.1492623,7.00548574 L12,7 Z M20.5425836,7.70208143 C21.4144016,7.1180865 22.5945711,7.35141313 23.178566,8.22323106 C23.3123319,8.4229237 23.4065884,8.64556331 23.4597758,8.90930817 L23.4919415,9.11510777 L23.5,9.28064743 L23.5,14.7186411 C23.5,15.7679821 22.649341,16.6186411 21.6,16.6186411 C21.359692,16.6186411 21.1223057,16.5730628 20.8736196,16.4705074 L20.6847641,16.3827296 L20.5427636,16.2973276 L17.8427636,14.4891546 C17.4300426,14.212758 17.1445442,13.7876818 17.0377508,13.2625835 L17.0088423,13.083913 L17,12.9104681 L17,11.0892663 C17,10.5926029 17.1942814,10.1189073 17.5710764,9.73799882 L17.7034184,9.61456165 L17.8425836,9.51070027 L20.5425836,7.70208143 Z M21.5,14.534 L21.501,9.466 L19,11.14 L18.998,12.856 L21.5,14.534 Z" id="Combined-Shape" ></path>
          </g>
      </svg>`;
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-video">${svgIcon}</span>${translation.VIDEO_EXTENSION}</div>`;
      this.translate.get("VIDEO_EXTENSION")
      .pipe(take(1))
        .subscribe(v => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-video">${svgIcon}</span> ${v} </div>`;
        });
      } else if (text.startsWith("==missed_call:")) {  // template for "missed call"
        const svgIcon =
        `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <title>Icon/product/arrow-call-missed</title>
            <g id="Icon/product/arrow-call-missed" stroke="none" stroke-width="1" fill-rule="evenodd">
                <g id="Icon-24px" transform="translate(3.000000, 7.000000)" >
                    <polygon id="Shape" points="16.59 0 9 7.59 3.41 2 8 2 8 0 0 0 0 8 2 8 2 3.41 9 10.41 18 1.41"></polygon>
                </g>
            </g>
        </svg>`;
        text = `<div class='message-icon'><span class="svg-icon-container icon-type-missed-call">${svgIcon}</span>${translation.MISSED_CALL}</div>`;
        this.translate.get("MISSED_CALL")
        .pipe(take(1))
          .subscribe(res => {
            text = `<div class='message-icon'><span class="svg-icon-container icon-type-missed-call">${svgIcon}</span> ${res} </div>`;
          });
        } else if ( text.startsWith("==ended_call:")) {  // template for "ended call"
          const svgIcon =
          `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
              <title>Icon/product/phone-call-hang-new</title>
              <g id="Icon/product/phone-call-hang-new" stroke="none" stroke-width="1" fill-rule="evenodd">
                  <g id="Icon-24px" transform="translate(1.000000, 6.000000)">
                      <path d="M0.718118899,4.12779989 C6.39726421,-1.3759333 15.6027358,-1.3759333 21.2818811,4.12779989 C22.1977429,5.01537339 22.237563,6.43435594 21.4013413,7.36813892 L21.2818811,7.49237006 L19.4124482,9.30406169 C18.4984787,10.1898014 17.0384269,10.2300623 16.0748135,9.42484437 L15.9406442,9.30406169 L15.7097621,9.09016092 C15.2395977,8.67392072 14.7188856,8.32691761 14.1449865,8.04679724 C13.3849238,7.66249118 12.8796751,6.92460021 12.8293886,6.1129924 L12.8256496,5.93805011 L12.8236986,4.92785585 L12.7526019,4.91379893 C11.7278494,4.73245394 10.6764706,4.71322365 9.64364868,4.85587652 L9.25729854,4.91695917 L9.18531475,4.93193216 L9.18475643,5.96425715 C9.17027171,6.73631033 8.77812057,7.43656001 8.1357651,7.87837694 L7.98314574,7.97566218 L7.83299366,8.0577341 C7.27796869,8.32848002 6.75995294,8.67414392 6.29017374,9.09019281 L5.95452868,9.39993074 C5.03728896,10.1912309 3.6506817,10.1999265 2.72312621,9.42601756 L2.58755183,9.30406169 L0.718118899,7.49237006 C-0.239372966,6.56445231 -0.239372966,5.05571764 0.718118899,4.12779989 Z M19.8900218,5.56401981 C14.9865413,0.811993396 7.01345875,0.811993396 2.1099782,5.56401981 C1.98428883,5.68582701 1.9663332,5.85598083 2.05611133,5.99160135 L2.1099782,6.05615013 L3.97941113,7.86784176 C4.13195512,8.01567411 4.38807229,8.04040391 4.54445861,7.96328676 L4.60478263,7.92406299 L4.66749653,7.86784176 C5.26063943,7.2930193 5.92456413,6.80717133 6.58911166,6.4485679 L6.96741533,6.25278908 C7.08366763,6.18425182 7.15175599,6.09579061 7.17534824,6.01853231 L7.18475672,5.96317547 L7.18619439,3.30495121 L8.90638203,2.94798543 C10.1548638,2.7254772 11.4325909,2.70216173 12.7134794,2.88368008 L13.1405235,2.95178038 L14.8205274,3.28394333 L14.8252823,5.97637387 C14.8235625,6.06611172 14.8998395,6.18734817 15.0222628,6.24946968 C15.6529921,6.5573289 16.2365263,6.93027198 16.7929478,7.38737179 L17.0689922,7.62302305 L17.3325035,7.86784176 C17.4916022,8.02202637 17.7598822,8.04129945 17.9462762,7.92566099 L18.0205889,7.86784176 L19.8900218,6.05615013 C20.0366594,5.91404173 20.0366594,5.70612821 19.8900218,5.56401981 Z" id="Shape"></path>
                  </g>
              </g>
          </svg>`;
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-ended-call">${svgIcon}</span>${translation.ENDED_CALL}</div>`;
          this.translate.get("ENDED_CALL")
          .pipe(take(1))
            .subscribe(res => {
              text = `<div class='message-icon'><span class="svg-icon-container icon-type-ended-call">${svgIcon}</span> ${res} </div>`;
            });
        }

    return text;
  }

  private _addPrefixForMessageWithLink(content): string {
    content = content.trim();
    let newContent = content;
    if (content.startsWith("http") || content.startsWith("https")) {
      const link = content.replace(/https?:\/\//ig , "").toLocaleLowerCase();
      newContent = "==link:" + content;
      if (link.split("/").length > 1) {
        const fileName = link.split("/").pop();
        if (fileName.split(".").length > 1) {
          const extension = fileName.split(".").pop();
          if (CommonUtil.isImage(extension)) {
            newContent = "==foto:" + content;
          } else if (CommonUtil.isAudio(extension)) {
            if (fileName.startsWith("audio_recording_")) {
              newContent = "==voicemessage:" + content;
            } else {
              newContent = "==audio:" + content;
            }
          } else if (CommonUtil.isVideo(extension)) {
            newContent = "==video:" + content;
          }
        }
      }
    }
    return newContent;
  }

  public logout() {
    this.logger.info("[AppService][logout]");

    localStorage.removeItem("unreadCounts"); // TODO: can remove it cause we call below   this.configService.clearStorage();
    this.xmppService.logout();
    // this.channelCableService.disconnect();

    this.broadcaster.broadcast("USER_LOGGED_OUT", true);

    // Web
    if (!this.isCordovaOrElectron) {
      this.dispatchLogoutEvents();

      this.configService.clearStorage();

      window.location.href = "/api/call-logout";
      return;
    } else {
      // Electron
      if (environment.isElectron) {
        this.databaseService.clearIndexedDB();
        this.configService.clearStorage();
        this.electronService.clearStorage();

      // Mobile
      } else {
        this.deleteFirebaseToken().subscribe();
        const firebaseToken = localStorage.getItem(ConstantsUtil.KEY_FIREBASE_TOKEN);
        this.logger.info("[firebaseToken][removeFirebaseToken]", firebaseToken);
        if (firebaseToken) {
          this.removeFirebaseToken(firebaseToken).subscribe(res => {
            window.FirebasePlugin.unregister();
            this.logger.info("[removeFirebaseToken]", firebaseToken, res);
          }, err => {
            window.FirebasePlugin.unregister();
            this.logger.sentryErrorLog("[removeFirebaseToken - error]", JSON.stringify(err));
            this.logger.error("[removeFirebaseToken - error]", err);
          });
        }
        this.configService.clearStorage();

        if (environment.shouldLogoutWithParam) {
          this.logoutWithParam();
        }
      }

      this.removeAuthTokenFromPreferences();
      this.removeUserJidFromPreferences();

      // DB drop for HIN only
      // or other apps it is only cleared on changing server URL,
      // you can not simply log out and select different username.
      if (this.isHinTheme()) {
        this.databaseService.deleteDatabase().subscribe();
      }

      if (CommonUtil.isOnAndroid()){
        let conversationIds;
        this.store.select(getConversations)
          .pipe(filter(convs => convs && convs.length > 0)
          , take(1))
          .subscribe(convs => conversationIds = convs.map(conv => conv.Target));

        if (conversationIds && conversationIds.length > 0) {
          this.removeLocalNotificationsForTarget(conversationIds)
            .subscribe(() => {
              this.redirectToMainPage();
            });
        } else {
          this.redirectToMainPage();
        }
      } else {
        this.redirectToMainPage();
      }

      this.dispatchLogoutEvents();
    }
  }

  private isHinTheme() {
    const isHin = localStorage.getItem("theme") === "hin" || environment.theme === "hin";
    this.logger.info("[AppService][isHinTheme]", isHin);
    return isHin;
  }

  private logoutWithParam() {
    const serverURL = localStorage.getItem("serverURL");
    this.logger.info("[AppService][logoutWithParam]", serverURL);

    const xhr = new XMLHttpRequest();
    xhr.open("GET", serverURL + "/?logout");
    xhr.withCredentials = true;
    xhr.responseType = "blob"; // force the HTTP response, response-type header to be blob
    xhr.onreadystatechange =  () => {
      this.logger.info("[AppService][logoutWithParam] resp", JSON.stringify(xhr.response), xhr.status, xhr.readyState);
    };
    xhr.send();
  }

  private redirectToMainPage(){
    this.logger.info("[LOGOUT] redirecting to main page ", environment.isCordova, window.location.href);
    // let initialHref = CommonUtil.isOnAndroid() ? window.location.href.split("/www")[0] : window.location.href.split("/talk")[0];
    let initialHref = window.location.href.split("/talk")[0];
    this.logger.info("redirectToMainPage initialHref: ", initialHref);
    const newHref = `${initialHref}/index.html`;
    this.logger.info("redirectToMainPage newHref: ", newHref);
    window.location.href = newHref;
  }

  private dispatchLogoutEvents() {
    this.store.dispatch(new Logout());
    this.store.dispatch(new LogoutSuccess());
  }

  private pullFirebaseTokenFromStorage() {
    let token = localStorage.getItem(ConstantsUtil.KEY_FIREBASE_TOKEN);

    if (token) {
      this.store.dispatch(new SetFirebaseToken(token));
    }
  }

  private pushFirebaseTokenToStore(token: string) {
    this.store.dispatch(new SetFirebaseToken(token));
    localStorage.setItem(ConstantsUtil.KEY_FIREBASE_TOKEN , token);
  }

  private pullServerURLFromStorage() {
    const url = localStorage.getItem(ConstantsUtil.KEY_SERVER_URL) || "";
    this.logger.info("[app.service] pullServerURLFromStorage" , url);
    this.store.dispatch(new SetBaseApiUrl(url));
  }

  public onXmppConnect(): Observable<boolean> {
    return this.store.select(getIsConnectedXMPP).pipe(filter(v => !!v));
  }

  public renderEmoji(input: string): SafeHtml {
    if (!input) {
      return "";
    }
    wdtEmojiBundle = window.wdtEmojiBundle || {};
    if (wdtEmojiBundle && wdtEmojiBundle.emoji) {
      wdtEmojiBundle.emoji.replace_mode = "unified";
      wdtEmojiBundle.emoji.allow_native = true;
      const content = wdtEmojiBundle.render(input.replace(/<br \/?>/g, " ____<br />____ "));
      return this.domSanitizer.bypassSecurityTrustHtml(content.replace(new RegExp(" ____<br />____ ", "g"), "<br />"));
    } else {
      return input;
    }
  }

  public prepareConversationContent(content: string): SafeHtml {
    if (!content) {
      return "";
    }
    content = CommonUtil.processHTMLBody(content);
    const repliedMessage = content.split(ConstantsUtil.REPLY_MESSAGE_LINE_BREAK);
    if (repliedMessage.length > 1) {
      content = repliedMessage[repliedMessage?.length - 1];
    }
    return this.renderEmoji(this.sanitizeHTML(content).replace(/&amp;nbsp;/ig, " ").replace(/&lt;/ig, "<")
    .replace(/&gt;/ig, ">").replace(/&amp;lt;/ig, "&lt;").replace(/&amp;gt;/ig, "&gt;")
    .replace(/&quote;/g, "\"")
    .replace(/&trade;/g, "™")
    .replace(/&copy;/g, "©")
    .replace(/&amp;/ig, "&")
    .replace(/&amp;amp;quote;/g, "&quote;")
    .replace(/&amp;amp;trade;/g, "&trade;")
    .replace(/&amp;amp;copy;/g, "&copy;")
    .replace(/&amp;amp;/ig, "&amp;")
    );
  }

  public getBackButtonListener() {
    return this._backButtonListener.asObservable();
  }


  public updateTitle() {
    return this.store.select(getTotalUnreadCount)
      .pipe(switchMap(count => {
        if (count > 0) {
          return timer(0 , 1000).pipe(map(() => {
            this.displayUnread = !this.displayUnread;
            return count;
          }));
        } else {
           document.title = environment.title;
          return empty();
        }
      })).subscribe(count => {
        if (this.displayUnread) {
          document.title = `Unread\t(${count})`;
        } else {
          document.title = environment.title;
        }
      });
  }

  private checkWindowFocused() {
    merge(
      fromEvent(window , "focus").pipe(map(() => true)) ,
      fromEvent(window , "blur").pipe(map(() => false)))
      .subscribe((focused) => {
        // this.logger.info("[AppService][checkWindowFocused] focused", focused);
        this.store.dispatch(new WindowFocusStatus(focused));
        if (focused) {
          this.store.select(getSelectedConversation).pipe(take(1)).subscribe(conv => {
            if (conv?.Target) {
              this.storeTalk.select(state => getConversationUnreadCount(state, conv)).pipe(take(1)).subscribe(count => {
                // this.logger.info("[AppService] getConversationUnreadCount", conv.Target, count);

                if (count > 0) {
                  this.store.dispatch(new ConversationResetUnread(conv.Target));
                  this.xmppService.sendMarkReadSignalToSelf(conv.Target);
                  this.conversationService.markConversationsRead(conv.Target).subscribe(() => {

                  }, () => {});
                }
              });
            }
          });
        }
      });
  }

  saveAuthTokenToPreferences(token) {
    if (!token) {
      return;
    }
    if (!environment.isCordova) {
      return;
    }
    if (!plugins) {
      return;
    }

    if (plugins.appPreferences) {
      plugins.appPreferences.store(() => {
        this.logger.info("[AppService][saveAuthTokenToPreferences] success");
      }, () => {
        this.logger.sentryErrorLog("[AppService][saveAuthTokenToPreferences] failure");
        this.logger.error("[AppService][saveAuthTokenToPreferences] failure");
      }, "auth-token", token);
    }
  }

  removeAuthTokenFromPreferences(){
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.remove(() => {
        this.logger.info("[AppService][removeAuthTokenFromPreferences] success");
      }, () => {
        this.logger.info("[AppService][removeAuthTokenFromPreferences] failure");
      }, "auth-token");
    }
  }

  saveUserJidToPreferences(jid) {
    if (!jid) {
      return;
    }
    if (!environment.isCordova) {
      return;
    }
    if (!plugins) {
      return;
    }

    if (plugins.appPreferences) {
      plugins.appPreferences.store(() => {
        this.logger.info("[AppService][saveUserJidToPreferences] success");
      }, () => {
        this.logger.sentryErrorLog("[AppService][saveUserJidToPreferences] failure");
        this.logger.error("[AppService][saveUserJidToPreferences] failure");
      }, "current_user_jid", jid);
    }
  }

  removeUserJidFromPreferences(){
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.remove(() => {
        this.logger.info("[AppService][removeUserJidFromPreferences] success");
      }, () => {
        this.logger.info("[AppService][removeUserJidFromPreferences] failure");
      }, "current_user_jid");
    }
  }

  saveApiUrlToPreferences() {
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      this.store.select(getBaseApiUrl).pipe(filter(url => !!url), take(1)).subscribe(base => {
        plugins.appPreferences.store(() => {
          this.logger.info("[AppService][saveApiUrlToPreferences] success");
        }, () => {
          this.logger.info("[AppService][saveApiUrlToPreferences] failure");
        }, "apiUrl", base + "/api");
      });
    }
  }

  removeApiUrlFromPreferences() {
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.remove(() => {
        this.logger.info("[AppService][removeApiUrlFromPreferences] success");
      }, () => {
        this.logger.info("[AppService][removeApiUrlFromPreferences] failure");
      }, "apiUrl");
    }
  }

  saveAvatarServiceUrlToPreferences() {
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      this.store.select(getBaseApiUrl).pipe(filter(url => !!url), take(1)).subscribe(() => {
        plugins.appPreferences.store(() => {
          this.logger.info("[AppService][saveAvatarServiceUrlToPreferences] success");
        }, () => {
          this.logger.info("[AppService][saveAvatarServiceUrlToPreferences] failure");
        }, "avatarServiceUrl", this.configService.avatarServiceUrl);
      });
    }
  }

  removeReplyMessages() {
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.remove(() => {
        this.logger.info("[AppService][removeReplyMessages] success");
      }, () => {
        this.logger.info("[AppService][removeReplyMessages] failure");
      }, "replyMessages"); // this constant comes from 'cordova-plugin-firebase'
    }
  }

  fetchReplyMessages(): Observable<any[]> {
    const response = new Subject<any[]>();
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.fetch((data) => {
        this.logger.info("[AppService][fetchReplyMessages] success", data);
        if (data && CommonUtil.isOnAndroid()) {
          data = JSON.parse(data);
        }
        response.next(data || []);
      }, () => {
        this.logger.info("[AppService][fetchReplyMessages] failure");
        response.next([]);
      }, "replyMessages"); // this constant comes from 'cordova-plugin-firebase'
    }
    return response.asObservable().pipe(take(1));
  }

  removeMarkAsReadFailedMessage() {
    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.remove(() => {
        this.logger.info("[AppService][removeMarkAsReadFailedMessage] success");
      }, () => {
        this.logger.info("[AppService][removeMarkAsReadFailedMessage] failure");
      }, "markAsReadFailedRequests"); // this constant comes from 'cordova-plugin-firebase'
    }
  }

  fetchMarkAsReadFailedMessage(): Observable<any[]> {
    const response = new Subject<any[]>();

    if (environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.fetch((data) => {
        this.logger.info("[AppService] fetchMarkAsReadFailedMessage success", data);
        if (data && CommonUtil.isOnAndroid()) {
          data = JSON.parse(data);
        }
        response.next(data || []);
      }, () => {
        this.logger.info("[AppService] fetchMarkAsReadFailedMessage failure");
        response.next([]);
      }, "markAsReadFailedRequests"); // this constant comes from 'cordova-plugin-firebase'
    }

    return response.asObservable().pipe(take(1));
  }

  removeAllNotificationsExceptForTargets(targets: string[]) {
    const response$ = new Subject();

    window.FirebasePlugin.clearTalkNotificationsExceptTargetsAndMissedCalls(targets.join(","), () => {
      response$.next(true);
    }, e => {
      this.logger.error("[AppService][removeAllNotificationsExceptForTargets]", e);
      this.logger.sentryErrorLog("[AppService][removeAllNotificationsExceptForTargets]", e);
      response$.next(false);
    });

    return response$.asObservable().pipe(take(1));
  }

  removeLocalNotificationsForTarget(targets: string[]) {
    const response$ = new Subject();

    let opsCountDone = 0;

    targets.forEach(target => {
      window.FirebasePlugin.clearNotificationsByTarget(target, () => {
        ++opsCountDone;
        if (opsCountDone === targets.length) {
          response$.next(true);
        }
      }, e => {
        this.logger.error("[AppService][removeLocalNotificationsForTarget]", e);
        CommonUtil.sentryLog("[removeLocalNotificationsForTarget]" + target + " - error: " + e);

        ++opsCountDone;
        if (opsCountDone === targets.length) {
          response$.next(false);
        }
      });
    });

    return response$.asObservable().pipe(take(1));
  }

  saveCurretRIngtoneToPreferences(currentRingtone: string) {
    this.logger.info("[AppService][saveCurretRIngtoneToPreferences]", currentRingtone);

    if (currentRingtone && environment.isCordova && !!plugins && plugins.appPreferences) {
      plugins.appPreferences.store(() => {
        this.logger.info("[AppService][saveCurretRIngtoneToPreferences] success", currentRingtone);
      }, () => {
        this.logger.info("[AppService][saveCurretRIngtoneToPreferences] failure");
      }, "currentRingtone", currentRingtone);
    }
  }

  changeFontSize(fontSize)
  {
    const previousValue = localStorage.getItem(ConstantsUtil.FONT_SIZE) || "14";
    const currentValue = fontSize;

    if (previousValue === currentValue) {
      return;
    }

    localStorage.setItem(ConstantsUtil.FONT_SIZE, currentValue);
    this.store.dispatch(new SetFontSize(parseInt(currentValue)));
    document.getElementById("main-html").classList.add("font-size-" + currentValue);
    document.getElementById("main-html").classList.remove("font-size-" + previousValue);
  }

  changeTheme(theme)
  {
    const previousTheme = localStorage.getItem(ConstantsUtil.THEME) || environment.theme;
    const currentTheme = theme;

    if (previousTheme === currentTheme)
    {
      return;
    }

    localStorage.setItem(ConstantsUtil.THEME, currentTheme);
    if (environment.isCordova) {
      if (CommonUtil.isOnAndroid()) {
        let initialHref = window.location.href.split("/talk")[0];
        this.logger.info("redirectToMainPage initialHref: ", initialHref);
        const newHref = `${initialHref}/index.html`;
        this.logger.info("redirectToMainPage newHref: ", newHref);
        window.location.href = newHref;
      } else {
        let initialHref = window.location.href.split("/talk")[0];
        window.location.href = `${initialHref}/index.html`;
      }
    } else if (environment.isElectron) {
      let initialHref = window.location.href.split("/talk")[0];
      window.location.href = `${initialHref}/index.html`;
    } else {
      self.location.reload();
    }
  }

  changeChatBackground(selectedBackground: string, previewOnly: boolean = false)
  {
    const previousChatBackground = localStorage.getItem(ConstantsUtil.CHAT_BACKGROUND) || environment.chatBackground;

    this.store.dispatch(new SetSelectedChatBackgroundPreview(selectedBackground));
    if (previousChatBackground === selectedBackground)
    {
      return;
    }
    if (!previewOnly) {
      localStorage.setItem(ConstantsUtil.CHAT_BACKGROUND, selectedBackground);
    }
  }


  storePendingMarkReadRequests(requests)
  {
    localStorage.setItem("read_requests", JSON.stringify(requests));
  }

  storePendingMarkPadReadRequests(requests)
  {
    localStorage.setItem("pad_read_requests", JSON.stringify(requests));
  }

  getPendingMarkReadRequests()
  {
    let userJid;
    let response = {};
    this.store.select(getUserJID).subscribe(jid => userJid = jid);

    if (userJid && localStorage.getItem("read_requests")) {
      try {
        response = JSON.parse(localStorage.getItem("read_requests" || "{}"));
      }
      catch (e) {
        response = {};
      }
    }
    else {
      this.storePendingMarkReadRequests({});
    }

    return response;
  }

  getPendingMarkPadReadRequests()
  {
    let userJid;
    let response = {};
    this.store.select(getUserJID).subscribe(jid => userJid = jid);

    if (userJid && localStorage.getItem("pad_read_requests")) {
      try {
        response = JSON.parse(localStorage.getItem("pad_read_requests" || "{}"));
      }
      catch (e) {
        response = {};
      }
    }
    else {
      this.storePendingMarkPadReadRequests({});
    }

    return response;
  }

  checkInstalledApp(uri: string, appName: string): Observable<boolean> {
    const subject = new Subject<boolean>;
    if (environment.isCordova) {
      uri = uri.replace("main", "");
      if (uri.indexOf("vnccalendar") !== -1) {
        uri = "vncmail://";
      }
      uri = uri.replace("main", "");
      if (CommonUtil.isOnAndroid()) {
        uri = CommonUtil.getPackageName(appName);
      }
      this.logger.info("[checkInstalledApp] uri", uri);
      appAvailability.check(
        uri, // URI Scheme
        (data) => {
          this.logger.info("[checkInstalledApp] installed", uri, appName, data);
          subject.next(true);
        },
        (err) => {
          this.logger.info("[checkInstalledApp] not installed", uri, appName, err);
          subject.next(false);
        }
      );

    } else {
      subject.next(true);
    }
    return subject.asObservable().pipe(take(1));
  }

  async showInstallAppDialog(appName) {
    let dialogStyles = {
      "width": "70%",
      "min-width": "70%",
      "max-width": "70%",
      "height": "auto",
      "visibility": "visible"
    };
    const { InstallAppDialogComponent } = await import(
      "app/talk/shared/components/dialogs/install-app/install-app.component");
    this.dialog.open(InstallAppDialogComponent, Object.assign({
      data: {appName: appName},
      backdropClass: "vnctalk-form-backdrop",
      panelClass: "vnctalk-form-panel",
      disableClose: true,
      autoFocus: true
    }, dialogStyles));
  }

  removeFirebaseToken(token: string): Observable<any> {
    this.logger.info("[removeFirebaseToken]", token);
    const serverURL = localStorage.getItem("serverURL");
    return this.http.delete(`${serverURL}/api/remove_device_tokens/${token}`, { headers: this.getHeaders() })
    .pipe(map ( res => { return res; }));
  }

  // goal is to make a single call from mobile app
  // addFirebaseToken(token: string): Observable<any> {
  //   const osSlug = CommonUtil.isOnIOS() ? "ios" : "android";
  //   const serverURL = localStorage.getItem("serverURL");
  //   return this.http.post(serverURL + "/api/add_device_token", { "device_token": {"value": token, "os": osSlug} },
  //   { headers: this.getHeaders() }).pipe(map ( res => { return res; }));
  // }

  getHeaders(): HttpHeaders {
    if (!this.headers) {
      this.headers = new HttpHeaders({
        "Content-Type": "application/json",
        "Accept": "application/json"
      });
      if (this.isCordovaOrElectron) {
        let token = localStorage.getItem("token");
        if (token) {
          this.headers = this.headers.set("Authorization", localStorage.getItem("token"));
        }
      }
    } else {
      if (this.isCordovaOrElectron) {
        let token = localStorage.getItem("token");
        if (token) {
          this.headers = this.headers.set("Authorization", localStorage.getItem("token"));
        }
      }
    }
    return this.headers;
  }

  uploadChatBackgroundImage(body: any) {
    const serverURL = localStorage.getItem("serverURL");
    return this.http.post(serverURL + "/api/customimage", body,
    { headers: this.getHeaders() }).pipe(map ( res => { return res; }));
  }

  getChatBackgroundCustomImages() {
    const serverURL = localStorage.getItem("serverURL");
    return this.http.get(serverURL + "/api/listcustomimages", { headers: this.getHeaders() }).pipe(map ( res => { return res; }));
  }

  getChatBackgroundCustomImageById(id: any) {
    const serverURL = localStorage.getItem("serverURL");
    return this.http.get(serverURL + "/api/getcustomimage/" + id, { headers: this.getHeaders() }).pipe(map ( res => { return res; }));
  }

  deleteChatBackgroundImage(id: any) {
    const serverURL = localStorage.getItem("serverURL");
    return this.http.delete(serverURL + "/api/customimages/" + id, { headers: this.getHeaders() }).pipe(map ( res => { return res; }));
  }

  checkNSetDarkMode() {
    let darkMode = localStorage.getItem(ConstantsUtil.DARK_MODE);
    let body = document.getElementsByTagName("body");
    if (darkMode && darkMode == "true") {
      if (body.length) {
          body[0].classList.add("dark-mode");
      }
    } else {
      body[0].classList.remove("dark-mode");
      document.documentElement.classList.remove("dark-mode");
    }
  }

  getPreviewWithCollabora(body: any): Observable<any>  {
    const serverURL = localStorage.getItem("serverURL");
    return this.http.post(serverURL + "/api/getwopiurl", body, { headers: this.getHeaders() }).pipe(map ( res => { return res; }));
  }

  removeDarkMode() {
    let body = document.getElementsByTagName("body");
    if (body[0].classList.contains("dark-mode")) {
      body[0].classList.remove("dark-mode");
      document.documentElement.classList.remove("dark-mode");
    }
  }

  checkNSetDarkModeForAll() {
    const isInIframe = window.self !== window.top;
    if (isInIframe) {
      return;
    } else {
      this.checkNSetDarkModeForGlobalSetting();
      this.checkNSetDarkModeForFilePreview();
      this.checkNSetDarkMode();
      this.checkNSetDarkModeForGlobalSearch();
    }
  }


  removerDarkModeForAll() {
    this.removeDarkModeForGlobalSearch();
    this.removeDarkModeForSetting();
    this.removeDarkModeForFilePreview();
  }

  removeDarkModeForFilePreview() {
    let body = document.getElementsByTagName("body");
    if (body[0].classList.contains("dark-mode-file")) {
      body[0].classList.remove("dark-mode-file");
    }
  }

  checkNSetDarkModeForFilePreview() {
    let darkMode = localStorage.getItem(ConstantsUtil.DARK_MODE);
    let body = document.getElementsByTagName("body");
    if (darkMode && darkMode == "true") {
      if (body.length) {
          body[0].classList.add("dark-mode-file");
      }
    } else {
      body[0].classList.remove("dark-mode-file");
    }
  }

  checkNSetDarkModeForGlobalSearch() {
    let darkMode = localStorage.getItem(ConstantsUtil.DARK_MODE);
    let body = document.getElementsByTagName("body");
    if (darkMode && darkMode == "true") {
      if (body.length) {
          body[0].classList.add("dark-global-search");
      }
    } else {
      body[0].classList.remove("dark-global-search");
    }
  }

  removeDarkModeForGlobalSearch() {
    let body = document.getElementsByTagName("body");
    if (body[0].classList.contains("dark-global-search")) {
      body[0].classList.remove("dark-global-search");
    }
  }

  checkNSetDarkModeForGlobalSetting() {
    let darkMode = localStorage.getItem(ConstantsUtil.DARK_MODE);
    let body = document.getElementsByTagName("body");
    if (darkMode && darkMode == "true") {
      if (body.length) {
          body[0].classList.add("dark-setting");
      }
    } else {
      body[0].classList.remove("dark-setting");
    }
  }

  removeDarkModeForSetting() {
    let body = document.getElementsByTagName("body");
    if (body[0].classList.contains("dark-setting")) {
      body[0].classList.remove("dark-setting");
    }
  }
}
